import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { useNavigateBack } from '@/hooks/useNavigateBack'
import {
  Search, Database as DatabaseIcon, PanelsTopLeft,
  Download, Copy, Pencil, FileText, Workflow, Type, Hand,
  ChevronRight as ChevronRightIcon,
  ChevronLeft, ChevronRight, Minus, Plus,
  List, RotateCw, Settings, Maximize2,
} from 'lucide-react'
import { toast } from 'sonner'
import AppSidebar from '@/components/AppSidebar'
import { PdfJsViewer, type PdfJsViewerHandle, type PdfOutlineItem } from '@/components/PdfViewer/PdfJsViewer'
import { ZoomReadout } from '@/components/intueri/ZoomReadout'
import { ReviewTab } from '@/components/DocumentViewer/ReviewTab'
import { ExtractionsTab } from '@/components/DocumentViewer/ExtractionsTab'
import { RawTab } from '@/components/DocumentViewer/RawTab'
import { SummaryTab } from '@/components/DocumentViewer/SummaryTab'
import { DetailsTab } from '@/components/DocumentViewer/DetailsTab'
import FileNameWithCopy from '@/components/FileNameWithCopy'
import { useRenameDocument } from '@/hooks/useRenameDocument'
import { apiService } from '@/service/api'
import { useAppStore } from '@/store'
import { isAnnotatorRole } from '@/types/users'
import { formatPageText } from '@/utils/pageTextUtils'
import { getUserDisplayName } from '@/utils/userUtils'
import { cn } from '@/lib/utils'
import { useTranslation } from '@/i18n'
import type { DocumentRecord } from '@/types/catalog'
import { useResizablePanel } from './useResizablePanel'
import type { PageClassification } from '@/types/mixed-documents'

type Tab = 'review' | 'extractions' | 'raw' | 'summary' | 'details'

// `label` holds an i18n key resolved with t() at render time (FS-532).
const TABS: { id: Tab; label: string }[] = [
  { id: 'review', label: 'data_catalog:tabs.review' },
  { id: 'extractions', label: 'data_catalog:tabs.extractions' },
  { id: 'raw', label: 'data_catalog:tabs.raw' },
  { id: 'summary', label: 'data_catalog:tabs.ai_summary' },
  { id: 'details', label: 'data_catalog:tabs.details' },
]

const MAX_TEXT_CACHE_SIZE = 20

const getPageTextIndex = (pageNumber: number) => Math.max(0, pageNumber - 1)

/** Pick the best heading from a page's raw text.
 *  Priority: markdown heading → numbered section → first short title-like line */
function pickHeading(text: string): string {
  if (!text) return ''
  const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0)

  // Pass 1: look for markdown headings (# Title)
  for (const line of lines) {
    const md = line.match(/^#{1,4}\s+(.+)/)
    if (md) {
      const h = md[1].replace(/[*_`#]/g, '').trim()
      if (h.length > 2 && h.length < 100) return h
    }
  }

  // Pass 2: look for numbered sections (e.g. "2.4.3 Description", "1. Introduction")
  for (const line of lines) {
    const clean = line.replace(/[*_`#]/g, '').trim()
    if (/^\d+(\.\d+)*\.?\s+\S/.test(clean) && clean.length > 4 && clean.length < 100) {
      return clean
    }
  }

  // Pass 3: first line that looks like a title — not too short, not a full sentence,
  // not generic filler like "table on this page", "figure 1" etc.
  const skip = /^(table|figure|fig\.|page|note|image|chart|graph|source|see |the |a |an |this |it |if |when |there )/i
  for (const line of lines.slice(0, 8)) {
    const clean = line.replace(/[*_`#]/g, '').trim()
    if (clean.length < 5 || clean.length > 90) continue
    if (skip.test(clean)) continue
    // Skip lines that end with period (likely body text, not a heading)
    if (clean.endsWith('.') || clean.endsWith(',') || clean.endsWith(';')) continue
    return clean
  }

  // Last resort: just use the first non-trivial line
  for (const line of lines.slice(0, 5)) {
    const clean = line.replace(/[*_`#]/g, '').trim()
    if (clean.length >= 5 && clean.length < 80) return clean
  }

  return ''
}

// ─── Toolbar icon button ─────────────────────────────────────
const ToolbarBtn = ({
  onClick,
  disabled,
  title,
  children,
  className,
}: {
  onClick?: () => void
  disabled?: boolean
  title?: string
  children: React.ReactNode
  className?: string
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={cn(
      'size-8 flex items-center justify-center rounded-md border border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card transition-colors',
      disabled
        ? 'opacity-30 cursor-not-allowed'
        : 'hover:bg-gray-100 dark:hover:bg-dark-accent cursor-pointer',
      className,
    )}
  >
    {children}
  </button>
)

export default function DocumentPreviewV2() {
  const { t } = useTranslation()
  const { doc_id } = useParams()
  const [searchParams] = useSearchParams()
  const navigateBack = useNavigateBack()
  const navigate = useNavigate()
  const { user } = useAppStore()
  // Annotators are read-only — no file/text download from the preview.
  const isAnnotator = isAnnotatorRole(user?.role)
  const boardNameParam = searchParams.get('boardName')
  const docNameParam = searchParams.get('docName')
  // ?embed=1 hides the AppSidebar + breadcrumb header so the same viewer
  // can render inside an iframe overlay (e.g. KG preview floating dialog)
  // without dragging the surrounding app chrome in.
  const isEmbedded = searchParams.get('embed') === '1'

  const cameFromBoard = typeof document !== 'undefined' && window.document.referrer && (() => {
    try {
      const ref = new URL(window.document.referrer)
      return ref.pathname.startsWith('/board/')
    } catch { return false }
  })()
  const boardName = boardNameParam || (cameFromBoard ? 'Board' : null)

  // Refs
  const pdfViewerRef = useRef<PdfJsViewerHandle>(null)
  const fullscreenRef = useRef<HTMLDivElement>(null)

  // Document state
  const [doc, setDoc] = useState<DocumentRecord | null>(null)
  const [docName, setDocName] = useState(docNameParam || '')

  // UI-08: the parsed-content panel was pinned at 420px, so wide extraction
  // tables and long raw lines could not be read without scrolling inside a
  // fixed column. 320 keeps the tab strip usable; 900 stops it swallowing the
  // page it is meant to sit beside.
  const sidePanel = useResizablePanel({
    defaultWidth: 420,
    // UI-55 (Putra): "fill the space, why not make this bigger when user open".
    // 420px left the panel a narrow strip beside a mostly-empty document pane
    // on a wide monitor. Two fifths of the row instead — ~768px at 1920, still
    // 420 on a laptop once the clamps bite, and a dragged width always wins.
    defaultFraction: 0.4,
    min: 320,
    max: 900,
    storageKey: 'documentPreview.sidePanelWidth',
    // The row holding this panel and the page view. The ceiling is derived
    // from its live width, so a persisted 900px cannot strand the document on
    // a narrow window or once the 220px outline opens.
    containerRef: fullscreenRef,
    minComplementWidth: 480,
  })
  const [pdfUrl, setPdfUrl] = useState('')
  const [loadingPdf, setLoadingPdf] = useState(true)

  // Page state
  const [currentPage, setCurrentPage] = useState(() => {
    const p = searchParams.get('page')
    return p ? parseInt(p, 10) : 1
  })
  const [totalPages, setTotalPages] = useState(0)
  const [pageInputValue, setPageInputValue] = useState('1')

  // Zoom
  const [zoomPercent, setZoomPercent] = useState(50)

  // Text/tab state
  const [activeTab, setActiveTab] = useState<Tab>('review')
  const [pageText, setPageText] = useState('')
  const [loadingText, setLoadingText] = useState(false)
  const pageTextCacheRef = useRef<Map<number, { text: string; isFormatted: boolean }>>(new Map())
  const editedPagesRef = useRef<Set<number>>(new Set())

  // TOC state
  const [showToc, setShowToc] = useState(false)
  const [outlineItems, setOutlineItems] = useState<PdfOutlineItem[]>([])

  // Right panel search
  const [panelSearch, setPanelSearch] = useState('')

  // Expanded mode (hide right panel)
  const [isExpanded, setIsExpanded] = useState(false)
  // Text selection mode (highlight/select text in PDF)
  const [isTextMode, setIsTextMode] = useState(false)
  const [isDocSearchOpen, setIsDocSearchOpen] = useState(false)

  // Page classification
  const [pageClassifications, setPageClassifications] = useState<PageClassification[]>([])
  const [showMarkDropdown, setShowMarkDropdown] = useState(false)

  // Document metadata for Details tab
  const [documentMetadata, setDocumentMetadata] = useState<{
    uploadedBy?: string; uploadDate?: string; lastModified?: string;
    fileSize?: string; documentType?: string;
  }>()

  // Edit mode for Review tab
  const [isEditing, setIsEditing] = useState(false)

  // Keep page input in sync
  useEffect(() => {
    setPageInputValue(String(currentPage))
  }, [currentPage])

  // Fullscreen toggle — uses browser Fullscreen API + hides panels
  const toggleFullscreen = useCallback(async () => {
    if (!fullscreenRef.current) return
    try {
      if (!window.document.fullscreenElement) {
        await fullscreenRef.current.requestFullscreen()
        setIsExpanded(true)
      } else {
        await window.document.exitFullscreen()
        setIsExpanded(false)
      }
    } catch {
      // Fallback: just toggle panel visibility if fullscreen API fails
      setIsExpanded(prev => !prev)
    }
  }, [])

  // Sync isExpanded when user exits fullscreen via Escape key
  useEffect(() => {
    const handler = () => {
      if (!window.document.fullscreenElement) setIsExpanded(false)
    }
    window.document.addEventListener('fullscreenchange', handler)
    return () => window.document.removeEventListener('fullscreenchange', handler)
  }, [])

  // ── Fetch document ───────────────────────────────────────────
  useEffect(() => {
    // UI-41 / greptile: this effect fetches the name asynchronously, so on a
    // navigation `docName` still holds the PREVIOUS document's until it lands.
    // The raw export reads it immediately, so clear it rather than let one
    // document's text download under another's name. Cleared to '' and not to
    // `docNameParam`: the export already reads `docName || docNameParam`, and
    // the param comes from the CURRENT url so it is never stale — referencing
    // it here would only add an effect dependency that re-runs this fetch.
    setDocName('')

    if (!doc_id) return
    let mounted = true

    const load = async () => {
      try {
        const docData = await apiService.getDocument(doc_id)
        if (!mounted) return
        setDoc(docData)
        setDocName(docData.name || 'Untitled')

        const uploaderName = getUserDisplayName(docData.user_id, user)
        setDocumentMetadata({
          uploadedBy: uploaderName || docData.user_id || 'Unknown',
          uploadDate: docData.upload_started_at || docData.created_at,
          lastModified: docData.updated_at || docData.created_at,
          fileSize: docData.file_size
            ? `${(docData.file_size / 1024).toFixed(1)} KB`
            : undefined,
          documentType: docData.type
            ? docData.type.charAt(0).toUpperCase() + docData.type.slice(1)
            : 'Document',
        })

        const pdfBlob = await apiService.downloadDocument(doc_id)
        if (!mounted) return
        const url = URL.createObjectURL(pdfBlob)
        setPdfUrl(url)
        setLoadingPdf(false)
      } catch (err) {
        console.error('Failed to load document', err)
        if (mounted) setLoadingPdf(false)
      }
    }
    load()
    return () => { mounted = false }
  }, [doc_id, user])

  useEffect(() => {
    return () => {
      if (pdfUrl && pdfUrl.startsWith('blob:')) URL.revokeObjectURL(pdfUrl)
    }
  }, [pdfUrl])

  // ── Fetch page classifications ──────────────────────────────
  useEffect(() => {
    if (!doc_id) return
    apiService.getPageClassifications(doc_id)
      .then(setPageClassifications)
      .catch(() => {/* ignore */})
  }, [doc_id])

  // Callback: PdfJsViewer extracted the full outline from the loaded PDF
  const handleOutlineExtracted = useCallback((items: PdfOutlineItem[]) => {
    setOutlineItems(items)
  }, [])

  // ── Build TOC from page text when PDF has no bookmarks ──────
  const [pageHeadings, setPageHeadings] = useState<Map<number, string>>(new Map())

  useEffect(() => {
    // Only run when we know there's no outline AND we know the total pages
    if (!doc_id || !totalPages || outlineItems.length > 0) return
    let cancelled = false

    const fetchHeadings = async () => {
      const headings = new Map<number, string>()
      // Fetch all pages in parallel batches of 10
      for (let start = 1; start <= totalPages && !cancelled; start += 10) {
        const batch = Array.from(
          { length: Math.min(10, totalPages - start + 1) },
          (_, i) => start + i,
        )
        const results = await Promise.allSettled(
          batch.map(pg => apiService.getDocumentPageText(doc_id, getPageTextIndex(pg))),
        )
        for (let i = 0; i < results.length; i++) {
          const r = results[i]
          if (r.status !== 'fulfilled' || !r.value) continue
          const heading = pickHeading(r.value)
          if (heading) headings.set(batch[i], heading)
        }
        // Update progressively so items appear as they load
        if (!cancelled) setPageHeadings(new Map(headings))
      }
    }

    fetchHeadings()
    return () => { cancelled = true }
  }, [doc_id, totalPages, outlineItems.length])

  // ── Fetch page text ──────────────────────────────────────────
  useEffect(() => {
    if (!doc_id || !totalPages) return
    let cancelled = false

    const fetchText = async () => {
      const cached = pageTextCacheRef.current.get(currentPage)
      if (cached) {
        setPageText(cached.isFormatted ? cached.text : formatPageText(cached.text))
        return
      }
      setLoadingText(true)
      try {
        const raw = await apiService.getDocumentPageText(doc_id, getPageTextIndex(currentPage))
        if (cancelled) return
        const formatted = formatPageText(raw)
        setPageText(formatted)
        if (pageTextCacheRef.current.size >= MAX_TEXT_CACHE_SIZE) {
          const oldest = pageTextCacheRef.current.keys().next().value
          if (oldest !== undefined) pageTextCacheRef.current.delete(oldest)
        }
        pageTextCacheRef.current.set(currentPage, { text: formatted, isFormatted: true })
      } catch {
        if (!cancelled) setPageText('')
      } finally {
        if (!cancelled) setLoadingText(false)
      }
    }
    fetchText()
    return () => { cancelled = true }
  }, [doc_id, currentPage, totalPages])

  useEffect(() => { setIsEditing(false) }, [currentPage])


  const handleContentUpdate = useCallback((newContent: string, pageNumber: number) => {
    if (pageNumber === currentPage) setPageText(newContent)
    editedPagesRef.current.add(pageNumber)
    pageTextCacheRef.current.set(pageNumber, { text: newContent, isFormatted: true })
  }, [currentPage])

  const handleVisualElementClick = useCallback((pageNumber: number) => setCurrentPage(pageNumber), [])

  // ── Toolbar actions ─────────────────────────────────────────
  const handlePageInputSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return
    const pg = parseInt(pageInputValue, 10)
    if (!isNaN(pg) && pg >= 1 && pg <= totalPages) setCurrentPage(pg)
    else setPageInputValue(String(currentPage))
  }

  const handleMarkPage = async (pageType: 'pnid' | 'other') => {
    if (!doc_id) return
    setShowMarkDropdown(false)
    try {
      await apiService.updatePageClassification(doc_id, currentPage, pageType)
      toast.success(t('data_catalog:preview.page_marked_success', {
        currentPage,
        pageType: pageType === 'pnid' ? t('data_catalog:preview.page_type_pnid') : t('data_catalog:preview.page_type_text'),
      }))
      const updated = await apiService.getPageClassifications(doc_id)
      setPageClassifications(updated)
    } catch { toast.error(t('data_catalog:preview.mark_page_error')) }
  }

  const handleCopyPageText = () => {
    if (!pageText) return
    navigator.clipboard.writeText(pageText)
    toast.success(t('common:general.copy_success'))
  }

  const handleDownloadPageText = () => {
    if (!pageText) return
    const blob = new Blob([pageText], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = window.document.createElement('a')
    a.href = url
    a.download = `${docName || 'document'}_page_${currentPage}.txt`
    a.click()
    URL.revokeObjectURL(url)
  }

  const renameDocument = useRenameDocument()
  const handleRenameDoc = (newName: string) => {
    void renameDocument(doc_id, newName, {
      // Match the label actually shown in the breadcrumb (`docName || docNameParam`)
      // so a failed rename reverts to what the user saw, not an empty string.
      previousName: docName || docNameParam || '',
      applyName: (n) => {
        setDocName(n)
        setDoc((d) => (d ? { ...d, name: n } : d))
      },
    })
  }

  const currentClassification = pageClassifications.find(c => c.page_number === currentPage)

  // TOC: use outline if available, otherwise page headings from API
  const tocItems = outlineItems.length > 0
    ? outlineItems
    : Array.from({ length: totalPages }, (_, i) => ({
        title: pageHeadings.get(i + 1) || `Page ${i + 1}`,
        pageNumber: i + 1,
        level: 0,
      }))

  // ── Render ───────────────────────────────────────────────────
  return (
    <div className="flex h-screen">
      {!isEmbedded && <AppSidebar />}

      <div className="flex flex-1 flex-col min-w-0">
        {/* ─── Breadcrumb navbar ─── */}
        {!isEmbedded && (
        <header className="flex h-[60px] shrink-0 items-center justify-between border-b border-[#e5e5e5] bg-white px-6 dark:border-dark-border dark:bg-dark-card">
          <div className="flex items-center gap-1.5 min-w-0 text-sm">
            {boardName ? (
              <PanelsTopLeft className="size-5 text-muted-foreground shrink-0" />
            ) : (
              <DatabaseIcon className="size-5 text-muted-foreground shrink-0" />
            )}
            <button
              onClick={boardName ? navigateBack : () => navigate('/data-catalog')}
              className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
            >
              {boardName || t('nav:menu.data', 'Data')}
            </button>
            <ChevronRightIcon className="size-4 text-muted-foreground shrink-0" />
            {docName || docNameParam ? (
              <FileNameWithCopy
                name={docName || docNameParam}
                className="!text-sm !font-medium text-foreground"
                iconClassName="size-4"
                onRename={doc ? handleRenameDoc : undefined}
              />
            ) : (
              <span className="font-medium text-foreground truncate">
                {t('common:general.loading')}
              </span>
            )}
          </div>
        </header>
        )}

        {/* ─── Main content — single layout, expand toggles panels via CSS ─── */}
        <div ref={fullscreenRef} className="flex flex-1 min-h-0 bg-white dark:bg-dark-card">

          {/* ─── Left side: Toolbar + TOC + PDF (always mounted) ─── */}
          <div className="flex flex-1 flex-col min-w-0">

            {/* ─── Toolbar ─── */}
            <div className="flex h-16 shrink-0 items-center justify-between border-b border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card px-4">
              <div className="flex items-center gap-2">
                <ToolbarBtn onClick={() => { if (isExpanded) { setIsExpanded(false); setShowToc(true) } else { setShowToc(!showToc) } }} title={t('data_catalog:preview.toc_title')}>
                  <List className="size-4 text-foreground" />
                </ToolbarBtn>
                <ToolbarBtn onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage <= 1} title={t('data_catalog:preview.prev_page')}>
                  <ChevronLeft className="size-4 text-foreground" />
                </ToolbarBtn>
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    value={pageInputValue}
                    onChange={(e) => setPageInputValue(e.target.value)}
                    onKeyDown={handlePageInputSubmit}
                    className="w-11 h-8 text-center text-sm border border-[#e5e5e5] dark:border-dark-border rounded-md bg-white dark:bg-dark-card focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <span className="text-sm text-foreground tabular-nums select-none">/</span>
                  <span className="inline-flex h-8 min-w-11 items-center justify-center rounded-md border border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card px-2 text-center text-sm text-foreground tabular-nums">
                    {totalPages || '—'}
                  </span>
                </div>
                <ToolbarBtn onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage >= totalPages} title={t('data_catalog:preview.next_page')}>
                  <ChevronRight className="size-4 text-foreground" />
                </ToolbarBtn>
              </div>
              <div className="flex items-center gap-2">
                <ToolbarBtn onClick={() => pdfViewerRef.current?.zoomOut()} title={t('data_catalog:preview.zoom_out')}><Minus className="size-4 text-foreground" /></ToolbarBtn>
                <ZoomReadout
                  percent={zoomPercent}
                  onSetPercent={(p) => pdfViewerRef.current?.setZoomPercent(p)}
                  label={t('common:zoom.level', { defaultValue: 'Zoom level' })}
                  className="inline-flex h-8 min-w-[52px] items-center justify-center rounded-md border border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card px-2 text-sm text-foreground tabular-nums cursor-text"
                  inputClassName="inline-flex h-8 min-w-[52px] items-center justify-center rounded-md border border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card px-2 text-sm text-foreground tabular-nums text-center outline-none focus:ring-2 focus:ring-blue-500"
                />
                <ToolbarBtn onClick={() => pdfViewerRef.current?.zoomIn()} title={t('data_catalog:preview.zoom_in')}><Plus className="size-4 text-foreground" /></ToolbarBtn>
              </div>
              <div className="flex items-center gap-2">
                <ToolbarBtn
                  onClick={() => {
                    if (isTextMode) { pdfViewerRef.current?.toggleTextMode(); setIsTextMode(false) }
                    if (isDocSearchOpen) { pdfViewerRef.current?.toggleSearch(); setIsDocSearchOpen(false) }
                  }}
                  title={t('data_catalog:preview.pan_mode')}
                  className={!isTextMode && !isDocSearchOpen ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700' : ''}
                ><Hand className="size-4 text-foreground" /></ToolbarBtn>
                <ToolbarBtn
                  onClick={() => {
                    if (!isTextMode) { pdfViewerRef.current?.toggleTextMode() }
                    if (isDocSearchOpen) { pdfViewerRef.current?.toggleSearch(); setIsDocSearchOpen(false) }
                    setIsTextMode(true)
                  }}
                  title={t('data_catalog:preview.text_select_mode')}
                  className={isTextMode ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700' : ''}
                ><Type className="size-4 text-foreground" /></ToolbarBtn>
                <ToolbarBtn
                  onClick={() => {
                    if (isTextMode) { pdfViewerRef.current?.toggleTextMode(); setIsTextMode(false) }
                    if (!isDocSearchOpen) { pdfViewerRef.current?.toggleSearch() }
                    setIsDocSearchOpen(true)
                  }}
                  title={t('data_catalog:preview.search_mode')}
                  className={isDocSearchOpen ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700' : ''}
                ><Search className="size-4 text-foreground" /></ToolbarBtn>
                <ToolbarBtn onClick={handleCopyPageText} disabled={!pageText} title={t('data_catalog:preview.copy_page_text')}><Copy className="size-4 text-foreground" /></ToolbarBtn>
                <ToolbarBtn onClick={() => pdfViewerRef.current?.rotateClockwise()} title={t('data_catalog:preview.rotate')}><RotateCw className="size-4 text-foreground" /></ToolbarBtn>
                {!isExpanded && (
                  <>
                    <div className="w-px h-6 bg-[#e5e5e5] dark:bg-dark-border mx-0.5" />
                    {!isAnnotator && <ToolbarBtn onClick={() => setShowMarkDropdown(!showMarkDropdown)} title={t('data_catalog:preview.mark_page')}><Settings className="size-4 text-foreground" /></ToolbarBtn>}
                  </>
                )}
                {!isAnnotator && <ToolbarBtn onClick={() => pdfViewerRef.current?.downloadPdf()} title={t('common:buttons.download')}><Download className="size-4 text-foreground" /></ToolbarBtn>}
                <ToolbarBtn onClick={toggleFullscreen} title={isExpanded ? t('data_catalog:preview.exit_fullscreen') : t('data_catalog:preview.fullscreen')}><Maximize2 className="size-4 text-foreground" /></ToolbarBtn>
              </div>
            </div>

            {/* ─── TOC + PDF ─── */}
            <div className="flex flex-1 min-h-0">
              {/* TOC sidebar — hidden in expanded */}
              {showToc && !isExpanded && (
                <div className="w-[220px] shrink-0 border-r border-[#e5e5e5] dark:border-dark-border flex flex-col bg-white dark:bg-dark-card">
                  {/* FS-584: no search box here — it was meant to search page
                      contents, which has no backend yet, so it read as a second
                      broken search next to the toolbar's working find-in-document. */}
                  <div className="flex-1 overflow-y-auto pt-2">
                    {tocItems.map((item, i) => (
                      <button
                        key={`${item.pageNumber}-${i}`}
                        onClick={() => setCurrentPage(item.pageNumber)}
                        className={cn(
                          'w-full text-left py-2 text-sm flex items-center justify-between gap-1',
                          item.pageNumber === currentPage
                            ? 'font-medium text-foreground'
                            : 'text-foreground/80 hover:text-foreground',
                        )}
                        style={{ paddingLeft: `${16 + item.level * 12}px`, paddingRight: '12px' }}
                      >
                        <span className="truncate min-w-0">{item.title}</span>
                        <span className="text-xs text-muted-foreground tabular-nums shrink-0 ml-1">{item.pageNumber}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* PDF viewer — single instance, never unmounts */}
              <div className="relative flex-1 flex flex-col min-w-0 overflow-hidden bg-[#f5f5f5] dark:bg-dark-muted">
                {loadingPdf ? (
                  <div className="h-full flex items-center justify-center">
                    <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
                  </div>
                ) : pdfUrl ? (
                  <PdfJsViewer
                    ref={pdfViewerRef}
                    url={pdfUrl}
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                    onTotalPagesChange={setTotalPages}
                    documentName={docName}
                    documentLocation={doc?.location ?? undefined}
                    documentId={doc_id}
                    hideToolbar
                    onZoomChange={setZoomPercent}
                    onOutlineExtracted={handleOutlineExtracted}
                  />
                ) : (
                  <div className="h-full flex items-center justify-center text-muted-foreground text-sm">{t('data_catalog:preview.load_error')}</div>
                )}
                {currentClassification && !isExpanded && !isAnnotator && (
                  <button type="button" onClick={() => setShowMarkDropdown(!showMarkDropdown)} className="absolute bottom-3 left-3 z-10">
                    <span className={cn(
                      'inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity',
                      currentClassification.page_type === 'pnid'
                        ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                        : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
                    )}>
                      {currentClassification.page_type === 'pnid' ? <><Workflow className="size-3" /> {t('data_catalog:preview.page_type_pnid')}</> : <><FileText className="size-3" /> {t('data_catalog:preview.page_type_text')}</>}
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ─── Right panel — hidden in expanded mode ─── */}
          {!isExpanded && (
            <div
              className="relative shrink-0 flex flex-col border-l border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card"
              style={{ width: sidePanel.width }}
            >
              {/* UI-08 drag handle. Sits ON the existing border and overhangs
                  it slightly so there is a real grab target without widening
                  the seam. `select-none` while dragging stops the pointer
                  selecting the document text underneath. */}
              <div
                {...sidePanel.handleProps}
                aria-label={t('data_catalog:preview.resize_panel', {
                  defaultValue: 'Resize panel',
                })}
                className={cn(
                  'absolute inset-y-0 -left-1 z-10 w-2 cursor-col-resize touch-none',
                  'hover:bg-primary/20 active:bg-primary/30 transition-colors',
                  'focus-visible:outline-none focus-visible:bg-primary/40',
                  sidePanel.isDragging && 'bg-primary/30',
                )}
              />
              <div className="flex h-16 shrink-0 items-end border-b border-[#e5e5e5] dark:border-dark-border px-4 overflow-x-auto">
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      'pb-3 px-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                      activeTab === tab.id
                        ? 'text-primary border-primary'
                        : 'text-muted-foreground border-transparent hover:text-foreground',
                    )}
                  >
                    {t(tab.label as never)}
                  </button>
                ))}
              </div>
              {(activeTab === 'review' || activeTab === 'raw') && (
                <div className="flex h-16 shrink-0 items-center justify-between border-b border-[#e5e5e5] dark:border-dark-border px-4 gap-3">
                  <div className="relative flex-1 max-w-[280px]">
                    <Search className="absolute start-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
                    <input type="text" placeholder={t('data_catalog:preview.search_placeholder')} value={panelSearch} onChange={(e) => setPanelSearch(e.target.value)}
                      className="w-full h-9 ps-8 pe-2 text-sm border border-[#e5e5e5] dark:border-dark-border rounded-md bg-transparent placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                  </div>
                  <div className="flex items-center gap-1.5">
                    {!isAnnotator && <ToolbarBtn onClick={handleDownloadPageText} disabled={!pageText} title={t('common:buttons.download')}><Download className="size-4 text-muted-foreground" /></ToolbarBtn>}
                    <ToolbarBtn onClick={handleCopyPageText} disabled={!pageText} title={t('data_catalog:preview.copy_button')}><Copy className="size-4 text-muted-foreground" /></ToolbarBtn>
                    {activeTab === 'review' && !isAnnotator && (
                      <button type="button" onClick={() => setIsEditing(!isEditing)}
                        className="h-8 px-3 flex items-center gap-1.5 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
                        <Pencil className="size-3.5" /> {t('common:buttons.edit')}
                      </button>
                    )}
                  </div>
                </div>
              )}
              <div className="flex-1 overflow-hidden">
                {loadingText && activeTab !== 'details' ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full" />
                  </div>
                ) : (
                  <>
                    {activeTab === 'review' && <ReviewTab pageText={pageText} docId={doc_id} currentPage={currentPage} onContentUpdate={handleContentUpdate} isEditing={isEditing} onEditingChange={setIsEditing} searchQuery={panelSearch} />}
                    {activeTab === 'extractions' && doc_id && <ExtractionsTab pageText={pageText} documentId={doc_id} currentPage={currentPage} onElementClick={handleVisualElementClick} searchQuery={panelSearch} />}
                    {activeTab === 'raw' && doc_id && <RawTab pageText={pageText} fileName={docName || docNameParam} />}
                    {activeTab === 'summary' && <SummaryTab docName={docName} pageText={pageText} docId={doc_id} currentPage={currentPage} />}
                    {activeTab === 'details' && <DetailsTab docName={docName} documentMetadata={documentMetadata} />}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mark Page As dropdown */}
      {showMarkDropdown && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowMarkDropdown(false)} />
          <div className="fixed z-50 bg-white dark:bg-dark-card border border-[#e5e5e5] dark:border-dark-border rounded-lg shadow-lg p-3 w-[280px]"
            style={{ top: '140px', left: '50%', transform: 'translateX(-50%)' }}
          >
            <p className="text-sm font-semibold text-foreground mb-3">{t('data_catalog:preview.mark_page_modal_title')}</p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleMarkPage('other')}
                className={cn(
                  'flex-1 flex flex-col items-center gap-1 p-3 rounded-lg border transition-colors',
                  currentClassification?.page_type === 'other'
                    ? 'border-primary bg-primary/5'
                    : 'border-[#e5e5e5] dark:border-dark-border hover:bg-muted/50',
                )}
              >
                <FileText className="size-4 text-muted-foreground" />
                <span className="text-xs font-medium">{t('data_catalog:preview.page_type_text')}</span>
              </button>
              <button
                type="button"
                onClick={() => handleMarkPage('pnid')}
                className={cn(
                  'flex-1 flex flex-col items-center gap-1 p-3 rounded-lg border transition-colors',
                  currentClassification?.page_type === 'pnid'
                    ? 'border-primary bg-primary/5'
                    : 'border-[#e5e5e5] dark:border-dark-border hover:bg-muted/50',
                )}
              >
                <Workflow className="size-4 text-muted-foreground" />
                <span className="text-xs font-medium">{t('data_catalog:preview.page_type_pnid')}</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
